import React from "react";
import { StyleSheet, View } from "react-native";
import { Input, Text } from "@ui-kitten/components";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { AppNavigatorScreenProps } from "../navigators/AppNavigatorScreenProps";
import sendPasswordReset from "../services/sendPasswordReset";
import emailValidator from "../helpers/validators";
import ScreenContainer from "../components/ScreenContainer";
import LoadingButton from "../components/LoadingButton";

type FormInput = {
    email: string;
};
const ResetPasswordScreen: React.FC<AppNavigatorScreenProps<"LoginScreen">> = ({
    navigation: { navigate },
}) => {
    const {
        control,
        handleSubmit,
        setError,
        formState: { isValid, isDirty },
    } = useForm<FormInput>({
        mode: "onChange",
        reValidateMode: "onChange",
    });
    const [isLoading, setIsLoading] = React.useState(false);
    const { t } = useTranslation();
    const onSubmit = React.useCallback(
        (data: FormInput) => {
            setIsLoading(true);
            sendPasswordReset(data.email)
                .then(() => {
                    toast.success(i18next.t("resetPassword.linkSent"));
                    navigate("LoginScreen");
                })
                .catch(() => {
                    setError("email", {
                        type: "validate",
                        message: i18next.t("login.incorrectEmail"),
                    });
                })
                .finally(() => setIsLoading(false));
        },
        [navigate, setError],
    );

    return (
        <ScreenContainer contentContainerStyle={styles.container}>
            <View style={styles.titleContainer}>
                <Text category="h3">{t("resetPassword.resetPassword")}</Text>
                <Text
                    category="p1"
                    appearance="hint"
                    style={
                        /* eslint-disable-next-line react-native/no-inline-styles */
                        {
                            // style inside StyleSheet.create (styles objects) doesn't work for Text component
                            textAlign: "center",
                            marginTop: 6,
                            padding: 6,
                        }
                    }
                >
                    {t("resetPassword.resetPasswordHint")}
                </Text>
            </View>

            <Controller
                control={control}
                render={({
                    field: { onChange, onBlur, value },
                    formState: { errors },
                }) => (
                    <View style={styles.inputMargin}>
                        <Input
                            label={t("labels.email")}
                            placeholder={t("labels.emailExample")}
                            value={value}
                            status={
                                errors.email?.type === "validate"
                                    ? "danger"
                                    : "basic"
                            }
                            onBlur={onBlur}
                            onChangeText={(nextValue) => onChange(nextValue)}
                        />
                        {errors.email?.type === "validate" && (
                            <View style={styles.errorMessage}>
                                <Text category="c1" status="danger">
                                    {errors.email.message}
                                </Text>
                            </View>
                        )}
                    </View>
                )}
                name="email"
                defaultValue=""
                rules={{ required: true, validate: emailValidator }}
            />
            <View style={styles.inputMargin}>
                <LoadingButton
                    isLoading={isLoading}
                    disabled={!isValid || !isDirty}
                    onPress={handleSubmit(onSubmit)}
                >
                    {t("resetPassword.sendLink")}
                </LoadingButton>
            </View>
        </ScreenContainer>
    );
};

const styles = StyleSheet.create({
    container: {
        marginTop: 50,
        flex: 1,
        alignItems: "center",
    },
    inputMargin: {
        marginTop: 15,
    },
    errorMessage: {
        marginTop: 4,
    },
    titleContainer: {
        paddingHorizontal: 20,
        alignItems: "center",
        justifyContent: "center",
    },
});
export default ResetPasswordScreen;
